import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetShell } from './SheetShell';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/**
 * GoalsBoard — #62 next-phase §2/§3 (UXeng, 2026-08-24). "A target is one number + one period" — cards,
 * not a table, and edit is a bottom sheet, not a detail panel (Goals has no separate detail view). Colour
 * is threshold-only (met vs. not — never a fabricated verdict/score, cmdr 2026-08-24): the bar is emerald
 * once actual >= target, neutral gray otherwise. 'No quota set' (target === null) and 'not tracked'
 * (actual === null, e.g. retention) are both honest-absence states, never a synthesized 0.
 */
interface GoalRow { id: string | null; metric: 'revenue' | 'newLeads' | 'retention'; period: string; target: number | null; actual: number | null; }
interface TopPerformer { ownerId: string; ownerName: string | null; wonValue: number }

const METRICS: GoalRow['metric'][] = ['revenue', 'newLeads', 'retention'];
const QUARTER_RE = /^\d{4}-Q[1-4]$/;

function formatValue(metric: string, v: number | null): string {
  if (v == null) return '—';
  if (metric === 'revenue') return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return String(v);
}

const GoalEditSheet: React.FC<{ row: GoalRow; shape: 'sheet' | 'popover'; anchor?: HTMLElement | null; onClose: () => void; onSaved: (row: GoalRow) => void }> = ({ row, shape, anchor, onClose, onSaved }) => {
  const { t } = useTranslation('common');
  const [period, setPeriod] = useState(row.period);
  const [target, setTarget] = useState(row.target != null ? String(row.target) : '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const num = Number(target);
    if (!QUARTER_RE.test(period.trim())) { setError(t('goalsBoard.errorPeriod')); return; }
    if (!target || Number.isNaN(num) || num <= 0) { setError(t('goalsBoard.errorTarget')); return; }
    setSaving(true);
    try {
      const res = await fetch('/api/goals', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metric: row.metric, period: period.trim(), target: num }),
      });
      if (!res.ok) throw new Error(String(res.status));
      onSaved({ ...row, id: row.id, period: period.trim(), target: num });
    } catch {
      setError(t('goalsBoard.errorSave'));
      setSaving(false);
    }
  };

  // The container (backdrop, shape, Escape/outside dismiss) is SheetShell — this used to be a
  // line-for-line fork of ValueSheet's container, which had already cost it Escape-dismiss and any
  // desktop shape (11-Goal D-G3: a bottom sheet at 1920px). Rendering through the shell un-forks it:
  // both fixes arrive from the shared code, and future shell fixes reach here by construction.
  return (
    <SheetShell shape={shape} anchor={anchor} onClose={onClose} className={shape === 'popover' ? 'w-80' : ''}>
      <form onSubmit={submit} data-testid={`goal-edit-sheet-${row.metric}`}
        className="p-4 flex flex-col gap-3">
        <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide">
          {t('goalsBoard.setTargetFor', { metric: t(`goalsBoard.metric.${row.metric}`) })}
        </div>
        <label className="flex flex-col gap-1 text-sm">
          {t('goalsBoard.period')}
          <input data-testid="goal-period-input" value={period} onChange={(e) => setPeriod(e.target.value)}
            placeholder="2026-Q3" dir="ltr" className="h-10 px-3 rounded-lg border border-border-neutral text-sm" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t('goalsBoard.target')}
          <input data-testid="goal-target-input" value={target} onChange={(e) => setTarget(e.target.value)}
            type="number" min="0" step="any" dir="ltr" className="h-10 px-3 rounded-lg border border-border-neutral text-sm" />
        </label>
        {error && <div data-testid="goal-edit-error" className="text-sm text-opt-rose">{error}</div>}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm font-semibold text-text-muted">{t('goalsBoard.cancel')}</button>
          <button type="submit" data-testid="goal-save" disabled={saving} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">
            {t('goalsBoard.save')}
          </button>
        </div>
      </form>
    </SheetShell>
  );
};

const GoalCard: React.FC<{ row: GoalRow; onEdit: (anchor: HTMLElement) => void }> = ({ row, onEdit }) => {
  const { t } = useTranslation('common');
  const hasTarget = row.target != null;
  const hasActual = row.actual != null;
  const pct = hasTarget && hasActual && (row.target as number) > 0 ? Math.min(100, Math.round(((row.actual as number) / (row.target as number)) * 100)) : null;
  const met = pct != null && pct >= 100;

  return (
    <div data-testid={`goal-card-${row.metric}`} className="bg-surface border border-border-neutral rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-label-caps uppercase text-text-muted">{t(`goalsBoard.metric.${row.metric}`)}</h3>
        <button type="button" data-testid={`goal-edit-${row.metric}`} onClick={(e) => onEdit(e.currentTarget)} className="text-xs font-semibold text-primary hover:underline">
          {hasTarget ? t('goalsBoard.edit') : t('goalsBoard.setTarget')}
        </button>
      </div>
      {!hasTarget ? (
        <div className="text-sm text-text-muted" data-testid={`goal-no-quota-${row.metric}`}>{t('goalsBoard.noQuotaSet')}</div>
      ) : (
        <>
          <div className="flex items-baseline gap-2" dir="ltr">
            <span className="text-2xl font-bold text-text-strong" data-testid={`goal-actual-${row.metric}`}>
              {hasActual ? formatValue(row.metric, row.actual) : t('goalsBoard.notTracked')}
            </span>
            <span className="text-sm text-text-muted">/ {formatValue(row.metric, row.target)}</span>
          </div>
          {hasActual && (
            <div className="h-2 rounded-full bg-canvas overflow-hidden">
              <div data-testid={`goal-progress-${row.metric}`} data-goal-met={met ? 'true' : 'false'}
                className={`h-full rounded-full ${met ? 'bg-opt-emerald' : 'bg-border-neutral'}`} style={{ width: `${pct}%` }} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const GoalsBoard: React.FC = () => {
  const { t } = useTranslation('common');
  // X-1 via useListFetch. The main load was the OTHER loading-forever variant (D-G1: failed load ->
  // eternal 'goals-loading'); the performers fetch was error-as-empty (D-G2: ".catch(() =>
  // setPerformers([]))" printed "nobody generated revenue" for a failed request — P23, the highest-
  // severity finding in its list). Both now have real error states; the ranking's failure renders
  // INSIDE the panel while the cards above stay live (failure is additive, never subtractive).
  const [rk, setRk] = useState(0);
  const main = useListFetch<{ period: string; goals: GoalRow[] }>('/api/goals', { refreshKey: rk });
  const period = main.data?.period ?? null;
  const goals = main.data?.goals ?? null;
  const setGoals = (fn: (gs: GoalRow[] | null) => GoalRow[]) =>
    main.setData((d) => (d ? { ...d, goals: fn(d.goals) } : d));
  const performersFetch = useListFetch<TopPerformer[]>(
    period ? `/api/goals/top-performers?period=${encodeURIComponent(period)}` : null,
    { refreshKey: rk });
  const performers = performersFetch.data;
  const [editing, setEditing] = useState<{ metric: GoalRow['metric']; anchor: HTMLElement } | null>(null);
  // Same viewport tier every other grid uses (>=1024 desktop). The editor is a bottom sheet on
  // phone/tablet and an anchored popover beside the Edit control on desktop (11-Goal D-G3).
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = () => setRk((k) => k + 1);

  if (main.status === 'error') {
    return <ListStateShell status="error" testIdBase="goals-board" onRetry={main.retry}>{null}</ListStateShell>;
  }
  if (!goals || !period) return <div className="p-6 text-sm text-text-muted" data-testid="goals-loading">{t('goalsBoard.loading')}</div>;

  const editingRow = editing ? (goals.find((g) => g.metric === editing.metric) ?? { id: null, metric: editing.metric, period, target: null, actual: null }) : null;

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="text-label-caps uppercase text-text-muted mb-3" data-testid="goals-period-heading">
        {t('goalsBoard.periodHeading', { period })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-testid="goals-cards">
        {METRICS.map((metric) => {
          const row = goals.find((g) => g.metric === metric) ?? { id: null, metric, period, target: null, actual: null };
          return <GoalCard key={metric} row={row} onEdit={(anchor) => setEditing({ metric, anchor })} />;
        })}
      </div>

      <div data-testid="goals-top-performers" className="bg-surface border border-border-neutral rounded-xl p-4">
        <h3 className="text-label-caps uppercase text-text-muted mb-3">{t('goalsBoard.revenueByOwner')}</h3>
        {performersFetch.status === 'error' ? (
          <div data-testid="goals-top-performers-error" className="flex flex-col items-start gap-2">
            <div className="text-sm text-text-muted">{t('listStates.error')}</div>
            <button type="button" data-testid="goals-top-performers-retry" onClick={performersFetch.retry}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('listStates.retry')}
            </button>
          </div>
        ) : performers == null ? (
          <div className="text-sm text-text-muted">{t('goalsBoard.loading')}</div>
        ) : performers.length === 0 ? (
          <div className="text-sm text-text-muted" data-testid="goals-top-performers-empty">{t('goalsBoard.revenueByOwnerEmpty')}</div>
        ) : (
          <ol className="space-y-2" data-testid="goals-top-performers-list">
            {performers.map((p, i) => (
              <li key={p.ownerId} className="flex items-center justify-between text-sm">
                <span className="text-text-strong">{i + 1}. {p.ownerName ?? '—'}</span>
                <span className="text-text-muted" dir="ltr">{p.wonValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {editingRow && (
        <GoalEditSheet row={editingRow} shape={isDesktop ? 'popover' : 'sheet'} anchor={editing?.anchor}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setGoals((gs) => (gs ?? []).some((g) => g.metric === saved.metric)
              ? (gs as GoalRow[]).map((g) => g.metric === saved.metric ? { ...g, target: saved.target, period: saved.period } : g)
              : [...(gs ?? []), saved]);
            setEditing(null);
            load();
          }} />
      )}
    </div>
  );
};
